/**
 * Chunked Video Uploader with Retry Logic
 * Phase 2: Enhanced with IndexedDB and Network Monitoring
 */

import chunkStorage from './chunkStorage';
import networkMonitor from './networkMonitor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

class ChunkedUploader {
  constructor(recordingId, videoType) {
    this.recordingId = recordingId;
    this.videoType = videoType; // 'camera' or 'screen'
    this.chunks = [];
    this.chunkIndex = 0;
    this.uploadedChunks = new Set();
    this.isUploading = false;
    this.onProgressCallback = null;
    this.onCompleteCallback = null;
    this.onErrorCallback = null;
    this.onNetworkStatusCallback = null;
    this.maxRetries = 3;
    this.retryDelay = 1000; // Start with 1 second
    this.isPaused = false;
    
    // Initialize storage
    this.initStorage();
    
    // Setup network monitoring
    this.setupNetworkMonitoring();
  }
  
  async initStorage() {
    try {
      await chunkStorage.init();
      console.log('✅ Chunk storage initialized');
      
      // Resume any pending uploads from previous session
      await this.resumePendingUploads();
    } catch (error) {
      console.error('❌ Failed to initialize storage:', error);
    }
  }
  
  setupNetworkMonitoring() {
    // Subscribe to network status changes
    networkMonitor.subscribe((isOnline) => {
      console.log(`📡 Network status: ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
      
      if (this.onNetworkStatusCallback) {
        this.onNetworkStatusCallback(isOnline);
      }
      
      if (!isOnline) {
        console.log('⏸️ Pausing uploads (offline)');
        this.isPaused = true;
      } else if (this.isPaused) {
        console.log('▶️ Resuming uploads (back online)');
        this.isPaused = false;
        this.startUploading();
      }
    });
    
    // Auto-resume on reconnect
    networkMonitor.onReconnect(() => {
      console.log('🔄 Network reconnected - resuming pending uploads');
      this.resumePendingUploads();
    });
  }
  
  async resumePendingUploads() {
    try {
      const pending = await chunkStorage.getPendingChunks(this.recordingId, this.videoType);
      
      if (pending.length > 0) {
        console.log(`🔄 Resuming ${pending.length} pending chunks`);
        
        // Add pending chunks to upload queue
        for (const chunk of pending) {
          this.chunks.push({
            index: chunk.chunk_index,
            blob: chunk.blob,
            size: chunk.size,
            timestamp: chunk.timestamp,
            retries: chunk.retry_count || 0
          });
        }
        
        // Start uploading if not already uploading
        if (!this.isUploading) {
          this.startUploading();
        }
      }
    } catch (error) {
      console.error('❌ Failed to resume pending uploads:', error);
    }
  }

  /**
   * Add a chunk to the upload queue
   */
  async addChunk(blob) {
    const chunk = {
      index: this.chunkIndex++,
      blob: blob,
      size: blob.size,
      timestamp: Date.now(),
      retries: 0
    };
    this.chunks.push(chunk);
    console.log(`📦 Chunk ${chunk.index} added (${(chunk.size / 1024 / 1024).toFixed(2)} MB)`);
    
    // Save to IndexedDB for resilience
    try {
      await chunkStorage.saveChunk(
        this.recordingId,
        this.videoType,
        chunk.index,
        blob,
        chunk.timestamp
      );
      console.log(`💾 Chunk ${chunk.index} saved to IndexedDB`);
    } catch (error) {
      console.error('⚠️ Failed to save chunk to IndexedDB:', error);
      // Continue anyway - not critical
    }
    
    // Start uploading if not already uploading
    if (!this.isUploading && !this.isPaused) {
      this.startUploading();
    }
  }

  /**
   * Start the upload process
   */
  async startUploading() {
    if (this.isUploading) return;
    this.isUploading = true;

    while (this.chunks.length > 0) {
      // Pause if offline
      if (this.isPaused || !networkMonitor.getStatus()) {
        console.log('⏸️ Upload paused (offline)');
        this.isUploading = false;
        return;
      }
      
      const chunk = this.chunks.shift();
      
      // Skip if already uploaded
      if (this.uploadedChunks.has(chunk.index)) {
        continue;
      }

      try {
        await this.uploadChunk(chunk);
        this.uploadedChunks.add(chunk.index);
        
        // Mark as uploaded in IndexedDB
        try {
          await chunkStorage.markChunkUploaded(this.recordingId, this.videoType, chunk.index);
        } catch (error) {
          console.error('⚠️ Failed to mark chunk as uploaded in IndexedDB:', error);
        }
        
        // Report progress
        if (this.onProgressCallback) {
          this.onProgressCallback({
            uploaded: this.uploadedChunks.size,
            total: this.chunkIndex,
            currentChunk: chunk.index
          });
        }
      } catch (error) {
        console.error(`❌ Failed to upload chunk ${chunk.index}:`, error);
        
        // Retry logic
        if (chunk.retries < this.maxRetries) {
          chunk.retries++;
          const delay = this.retryDelay * Math.pow(2, chunk.retries - 1); // Exponential backoff
          console.log(`🔄 Retrying chunk ${chunk.index} (attempt ${chunk.retries}/${this.maxRetries}) in ${delay}ms`);
          
          // Update retry count in IndexedDB
          try {
            await chunkStorage.incrementRetryCount(this.recordingId, this.videoType, chunk.index);
          } catch (error) {
            console.error('⚠️ Failed to update retry count in IndexedDB:', error);
          }
          
          await new Promise(resolve => setTimeout(resolve, delay));
          this.chunks.unshift(chunk); // Put it back at the front
        } else {
          console.error(`❌ Chunk ${chunk.index} failed after ${this.maxRetries} retries`);
          if (this.onErrorCallback) {
            this.onErrorCallback({
              chunk: chunk.index,
              error: 'Max retries exceeded'
            });
          }
        }
      }
    }

    this.isUploading = false;
    
    // All chunks uploaded successfully
    if (this.onCompleteCallback && this.chunks.length === 0) {
      this.onCompleteCallback({
        totalChunks: this.chunkIndex,
        totalSize: Array.from(this.uploadedChunks).reduce((sum, idx) => {
          const chunk = this.chunks.find(c => c.index === idx);
          return sum + (chunk ? chunk.size : 0);
        }, 0)
      });
    }
  }

  /**
   * Upload a single chunk with retry logic
   */
  async uploadChunk(chunk) {
    const formData = new FormData();
    formData.append('chunk', chunk.blob, `chunk_${chunk.index}.webm`);
    formData.append('chunk_index', chunk.index);
    formData.append('recording_id', this.recordingId);
    formData.append('video_type', this.videoType);
    formData.append('timestamp', chunk.timestamp);

    const token = localStorage.getItem('token');
    
    console.log(`⬆️ Uploading chunk ${chunk.index}...`);
    
    const response = await fetch(`${API}/student/upload-video-chunk`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ Chunk ${chunk.index} uploaded successfully`);
    return result;
  }

  /**
   * Set progress callback
   */
  onProgress(callback) {
    this.onProgressCallback = callback;
  }

  /**
   * Set completion callback
   */
  onComplete(callback) {
    this.onCompleteCallback = callback;
  }

  /**
   * Set error callback
   */
  onError(callback) {
    this.onErrorCallback = callback;
  }
  
  /**
   * Set network status callback
   */
  onNetworkStatus(callback) {
    this.onNetworkStatusCallback = callback;
  }
  
  /**
   * Get upload statistics
   */
  async getStats() {
    try {
      const allChunks = await chunkStorage.getAllChunks(this.recordingId, this.videoType);
      const uploaded = allChunks.filter(c => c.uploaded).length;
      const pending = allChunks.filter(c => !c.uploaded).length;
      const totalSize = allChunks.reduce((sum, c) => sum + c.size, 0);
      
      return {
        total: allChunks.length,
        uploaded,
        pending,
        totalSize,
        uploadedSize: allChunks.filter(c => c.uploaded).reduce((sum, c) => sum + c.size, 0)
      };
    } catch (error) {
      console.error('❌ Failed to get stats:', error);
      return null;
    }
  }
  
  /**
   * Pause uploads
   */
  pause() {
    this.isPaused = true;
    console.log('⏸️ Uploads paused manually');
  }
  
  /**
   * Resume uploads
   */
  resume() {
    this.isPaused = false;
    console.log('▶️ Uploads resumed manually');
    if (!this.isUploading) {
      this.startUploading();
    }
  }

  /**
   * Finalize the upload (tell backend all chunks are uploaded)
   * Waits for all pending uploads to complete before finalizing
   */
  async finalize() {
    const token = localStorage.getItem('token');
    
    console.log(`🏁 Starting finalize for ${this.videoType}...`);
    console.log(`📊 Total chunks recorded: ${this.chunkIndex}`);
    console.log(`📊 Uploaded chunks: ${this.uploadedChunks.size}`);
    console.log(`📊 Pending in queue: ${this.chunks.length}`);
    
    // Wait for all pending uploads to complete
    if (this.chunks.length > 0 || this.isUploading) {
      console.log('⏳ Waiting for pending uploads to complete...');
      
      // Set a timeout to prevent infinite waiting
      const maxWaitTime = 5 * 60 * 1000; // 5 minutes max wait
      const startTime = Date.now();
      
      while ((this.chunks.length > 0 || this.isUploading) && (Date.now() - startTime) < maxWaitTime) {
        // Start uploading if not already
        if (!this.isUploading && this.chunks.length > 0) {
          this.startUploading();
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log(`⏳ Waiting... pending: ${this.chunks.length}, uploading: ${this.isUploading}`);
      }
      
      if (this.chunks.length > 0) {
        console.warn(`⚠️ Timeout waiting for uploads. ${this.chunks.length} chunks still pending.`);
      }
    }
    
    // Check if we have any chunks uploaded
    if (this.uploadedChunks.size === 0 && this.chunkIndex === 0) {
      console.warn('⚠️ No chunks to finalize - recording may be empty');
      return { status: 'empty', message: 'No recording data' };
    }
    
    console.log(`🏁 Finalizing upload for ${this.videoType}...`);
    console.log(`📊 Final: ${this.uploadedChunks.size} chunks uploaded out of ${this.chunkIndex}`);
    
    const response = await fetch(`${API}/student/finalize-video-upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        recording_id: this.recordingId,
        video_type: this.videoType,
        total_chunks: this.chunkIndex,
        uploaded_chunks: this.uploadedChunks.size
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Finalization failed: ${response.status} - ${errorText}`);
      throw new Error(`Finalization failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ Upload finalized for ${this.videoType}`, result);
    
    // Clean up IndexedDB after successful finalization
    try {
      await chunkStorage.deleteRecording(this.recordingId);
      console.log('🧹 Cleaned up IndexedDB for recording');
    } catch (error) {
      console.error('⚠️ Failed to clean up IndexedDB:', error);
    }
    
    return result;
  }
}

export default ChunkedUploader;
